import dayjs from 'dayjs'

import { IForm } from 'core-components/classes/models/IForm'
import { sanitize } from 'core-components/helpers/sanitize'
import { IDataProviderFilter, IDataProviderSort } from 'core-components/types'
import { guid } from 'src/core/classes/helpers/GuidHelper'
import { getActiveTokens } from 'src/core/classes/helpers/StorageHelper'
import { proxy } from 'src/core/classes/helpers/UrlHelper'
import { HttpCancel, HttpHeaders, HttpRequest } from 'src/core/classes/http'
import Logger from 'src/core/utils/Logger'

export interface ICustomSSO {
  Locale?: string
  Language?: string
  CountryCode?: string
}

export class CrudHelper {
  private url: string
  private readonly searchUrl: string
  private readonly fromToUrl: string
  private readonly urlExport: string

  constructor(dataProviderId: number, code: string) {
    this.url = proxy(`/form/data-set-provider/v3/${dataProviderId}/${code}/kendo/data`)
    this.searchUrl = proxy(`/form/data-set-provider/v3/${dataProviderId}/${code}/kendo/data/search`)
    this.fromToUrl = proxy(`/form/data-set-provider/v3/${dataProviderId}/${code}/data/list`)
    this.urlExport = `/form/data-set-provider/v3/${dataProviderId}/${code}/export`
  }

  private getToken = () => {
    return getActiveTokens()[0]
  }

  public async create(data: object[], cancelObject?: HttpCancel, throwError: boolean = false) {
    const payload = { models: data }

    const headers = this.getAuthHeaders()
    const request = new HttpRequest(headers)

    const response = await request.post({ url: this.url, payload, cancelObject })
    if (this.isRequestFailed(response) && throwError) {
      throw new Error('Request Failed')
    }
    return response['data'] || []
  }

  public async read(
    filter?: IDataProviderFilter | IForm,
    sort?: IDataProviderSort | IForm,
    page?: number,
    pageSize?: number,
    cancelObject?: HttpCancel,
    applyAudieceFilter: boolean = false,
    skipTotal: boolean = false,
    customSSO?: ICustomSSO
  ): Promise<any> {
    const payload: IForm = {
      filter,
      sort,
      customSSO,
    }

    if (page !== undefined) {
      payload.page = page
    }
    if (pageSize !== undefined) {
      payload.pageSize = pageSize
    }

    payload.applyAudienceFilter = applyAudieceFilter
    payload.skipTotal = skipTotal

    const headers = this.getAuthHeaders()
    const request = new HttpRequest(headers)
    const response = await request.post({ url: this.searchUrl, payload, cancelObject })
    const responseData = response['data'] || {}
    if (responseData['Errors']) {
      Logger.error(sanitize(responseData['Errors']))
    }
    return responseData
  }

  public async readFromTo(
    filter?: IDataProviderFilter | IForm,
    sort?: IDataProviderSort | IForm,
    from?: number,
    to?: number,
    cancelObject?: HttpCancel,
    applyAudieceFilter: boolean = false,
    customSSO?: ICustomSSO,
    columns?: string[],
    skipTotal: boolean = false
  ): Promise<any> {
    const payload: IForm = {
      filter,
      sort,
      customSSO,
      columns,
    }

    if (from !== undefined) {
      payload.from = from
    }
    if (to !== undefined) {
      payload.to = to
    }

    payload.applyAudienceFilter = applyAudieceFilter
    if (skipTotal) {
      payload.skipTotal = skipTotal
    }

    const headers = this.getAuthHeaders()
    const request = new HttpRequest(headers)
    const response = await request.post({ url: this.fromToUrl, payload, cancelObject })
    const responseData = response['data'] || {}
    if (responseData['Errors']) {
      Logger.error(sanitize(responseData['Errors']))
    }
    return responseData
  }

  public async update(
    data: Array<{ EntryId: number; [key: string]: any }>,
    cancelObject?: HttpCancel,
    throwError: boolean = false
  ) {
    const payload = { models: data }

    const headers = this.getAuthHeaders()
    const request = new HttpRequest(headers)

    const response = await request.put({ url: this.url, payload, cancelObject })
    if (this.isRequestFailed(response) && throwError) {
      throw new Error('Create Request Failed')
    }
    return response['data'] || []
  }

  public async delete(
    data: Array<{ [key: string]: any }>,
    cancelObject?: HttpCancel,
    throwError: boolean = false
  ) {
    const payload = { models: data }

    const headers = this.getAuthHeaders()
    const request = new HttpRequest(headers)

    const responseData = await request.delete({ url: this.url, payload, cancelObject })
    if (this.isRequestFailed(responseData) && throwError) {
      throw new Error('Delete request Failed')
    }
    return responseData['data'] || []
  }

  public async export(
    columns: { title: string; name: string }[],
    filter?: IDataProviderFilter,
    format: 'xlsx' = 'xlsx'
  ) {
    const momentInstance = dayjs()
    const datetime = momentInstance.format('YYYY-MM-DD_hh_mm')
    const payload = {
      format,
      fileName: `${guid()}-product-finder-export-${datetime}`,
      filter,
      columns,
    }

    const headers = this.getAuthHeaders()
    const request = new HttpRequest(headers)

    const response = await request.post({ url: this.urlExport, payload })
    return response.data || []
  }

  private getAuthHeaders = () => {
    const headers = new HttpHeaders()
    headers.add('Content-Type', 'application/json; charset=UTF-8')
    const token = this.getToken()
    if (token) {
      headers.add('Authorization', `Bearer ${token}`)
    }
    return headers
  }

  private isRequestFailed = (response: IForm) => {
    return !Object.keys(response).length
  }
}
